/**
 * Merge admin-added (custom) language bundles into i18next after boot.
 *
 * Builtin locales (cs/en) ship compiled JSON; a custom locale's strings come
 * from ``GET /public/translation-bundles`` (``{lang: {ns: {key: value}}}``).
 * We ``addResourceBundle`` each one so a custom locale renders app-wide,
 * falling back to the default language for any untranslated key.
 *
 * Runs on every shell (unlike ``useShellI18nOverrides``, which is public-shell
 * only) — a custom locale must also localise the admin UI, not just the
 * marketing site.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { expandDottedKeys } from "@/i18n/expandDottedKeys";
import { apiFetch } from "@/lib/api";

type Bundles = Record<string, Record<string, Record<string, string>>>;

export function useCustomLanguageBundles(): void {
  const { i18n } = useTranslation();
  const { data } = useQuery<Bundles>({
    queryKey: ["public", "translation-bundles"],
    queryFn: () => apiFetch<Bundles>("/api/v1/public/translation-bundles"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const fingerprint = data ? JSON.stringify(data) : "";

  useEffect(() => {
    if (!data) return;
    let merged = false;
    for (const [lang, namespaces] of Object.entries(data)) {
      for (const [ns, keys] of Object.entries(namespaces)) {
        i18n.addResourceBundle(lang, ns, expandDottedKeys(keys), true, true);
        merged = true;
      }
    }
    // Re-render every useTranslation consumer (same event the overrides
    // merge uses — the bare ``added`` event isn't what react-i18next listens
    // to). Skip if nothing merged so we don't fire on the empty payload.
    if (merged) i18n.emit("languageChanged", i18n.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, i18n]);
}
